import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { clearAuthAndRedirectToLogin, clearAuthStorage, TOKEN_KEY, USER_KEY } from '../lib/authSession';

const API_BASE_URL =
  process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  roleDisplayName?: string;
  isActive: boolean;
  permissions?: Record<
    string,
    { view: boolean; create: boolean; edit: boolean; delete: boolean }
  >;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

/**
 * The sign-in context, or undefined when there is no AuthProvider above. For shared components
 * (the vendor picker) that only ask "may this person do X?" and must still render, answering
 * "no", on a screen mounted without a provider. Everything else uses useAuth / usePermission.
 */
export const useOptionalAuth = () => useContext(AuthContext);

// Key names come from the one owner (lib/authSession) — this file used to
// declare its own copies, which is how a second key name gets born.
const LS_TOKEN_KEY = TOKEN_KEY;
const LS_USER_KEY = USER_KEY;

const EXTRA_AUTH_KEYS = [
  'auth',
  'authToken',
  'accessToken',
  'jwt',
  'jwtToken',
  'refreshToken',
  'userData',
  'persist:root',
];

function safeJsonParse<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function isPublicAuthPath(url: string): boolean {
  return (
    url.includes('/users/login') ||
    url.includes('/users/forgot-password') ||
    url.includes('/users/reset-password')
  );
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const hydrate = () => {
    const t = localStorage.getItem(LS_TOKEN_KEY);
    const u = safeJsonParse<User>(localStorage.getItem(LS_USER_KEY));
    if (t && u) {
      setToken(t);
      setUser(u);
    } else {
      setToken(null);
      setUser(null);
    }
  };

  useEffect(() => {
    hydrate();

    const originalFetch = window.fetch.bind(window);
    (window as any).fetch = async (...args: any[]) => {
      const res: Response = await originalFetch(...(args as Parameters<typeof fetch>));
      if (res && res.status === 401) {
        const raw = args[0];
        const url = typeof raw === 'string' ? raw : (raw as Request)?.url || '';
        if (!isPublicAuthPath(url)) {
          clearAuthAndRedirectToLogin();
          setUser(null);
          setToken(null);
        }
      }
      return res;
    };

    (async () => {
      const t = localStorage.getItem(LS_TOKEN_KEY);
      if (t) {
        try {
          const res = await originalFetch(`${API_BASE_URL}/users/profile`, {
            headers: { Authorization: `Bearer ${t}` },
          });
          if (res.ok) {
            const json = await res.json();
            const fresh = json.data as User;
            setUser(fresh);
            setToken(t);
            localStorage.setItem(LS_USER_KEY, JSON.stringify(fresh));
          } else if (res.status === 401 || res.status === 403) {
            // The server has REFUSED this token — expired, revoked, or the
            // account deactivated. There used to be no else branch at all, so
            // nothing was cleared and the user hydrated from localStorage a few
            // lines above stayed authenticated: protected routes rendered from
            // stale state until some later request happened to 401.
            //
            // Only 401/403. A 500 or a 502 says nothing about whether the
            // session is valid, and logging someone out because the server had
            // a bad minute loses whatever they were in the middle of.
            // (TECH_DEBT #44)
            clearAuthAndRedirectToLogin();
            setUser(null);
            setToken(null);
          }
        } catch {
          // A network error, not an answer. The session may be perfectly good
          // and the laptop may have moved between wifi points, so this
          // deliberately does NOT sign anyone out — but it must not be silent
          // either, because a bare swallow made a blip indistinguishable from a
          // revoked session for anyone reading the console afterwards. The
          // fetch monkey-patch above still signs out on a real 401 from any
          // subsequent request, which is the backstop for a session that turns
          // out to be dead.
          console.warn('[auth] could not validate the stored session; keeping it until a request is refused');
        }
      }
      setIsLoading(false);
    })();

    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      const watched = new Set([LS_TOKEN_KEY, LS_USER_KEY, ...EXTRA_AUTH_KEYS]);
      if (watched.has(e.key)) hydrate();
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      (window as any).fetch = originalFetch;
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE_URL}/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) return false;

      const data = await res.json();
      const { token: authToken, user: userData } = data.data;

      setToken(authToken);
      setUser(userData);

      localStorage.setItem(LS_TOKEN_KEY, authToken);
      localStorage.setItem(LS_USER_KEY, JSON.stringify(userData));

      return true;
    } catch (err) {
      // Network/fetch failure (e.g. "Failed to fetch") — the request never reached
      // the server, so this is NOT a credential problem. Re-throw so the caller can
      // show a network-specific message. A 401 (wrong credentials) still returns
      // false above and shows "Invalid email or password".
      // eslint-disable-next-line no-console
      console.error('Login error:', err);
      throw err;
    }
  };

  const logout = () => {
    // One owner for the cleanup (lib/authSession) — the key list used to be
    // spelled here a second time.
    const { failed } = clearAuthStorage();
    if (failed.length) {
      // eslint-disable-next-line no-console
      console.warn('Logout storage clear error — could not clear:', failed.join(', '));
    }

    setUser(null);
    setToken(null);
  };

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      token,
      login,
      logout,
      isAuthenticated: !!user && !!token,
      isLoading,
    }),
    [user, token, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
